import { supabase } from '@/lib/supabase';
import { User, Session } from '@supabase/supabase-js';
import { BaseService } from './baseService';
import type { AuthResponse, UserCreationData, UserProfileData } from './types';

/**
 * Service for user and authentication-related data operations
 */
export class UserService extends BaseService {
  /**
   * Get the current authenticated user
   */
  static async getCurrentUser(): Promise<User | null> {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) throw error;
      return data.user;
    } catch (error) {
      this.handleApiError(error, 'Error fetching current user');
      return null; // This line is never reached due to throw in handleApiError, but needed for TypeScript
    }
  }

  /**
   * Get the current user session
   */
  static async getCurrentSession(): Promise<Session | null> {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data.session;
    } catch (error) {
      this.handleApiError(error, 'Error fetching current session');
      return null;
    }
  }

  /**
   * Get current user's role from metadata
   */
  static async getUserRole(): Promise<string | null> {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) throw error;
      // Fix: Use user_metadata instead of email
      return data.user?.user_metadata?.role || null;
    } catch (error) {
      this.handleApiError(error, 'Error fetching user role');
      return null;
    }
  }

  /**
   * Check if current user is an admin
   */
  static async isUserAdmin(): Promise<boolean> {
    try {
      const role = await this.getUserRole();
      const adminRoles = ['owner', 'bizadmin', 'admin', 'superadmin'];
      return adminRoles.includes(role || '');
    } catch (error) {
      this.handleApiError(error, 'Error checking admin status');
      return false;
    }
  }

  /**
   * Create a new user account
   */
  static async createUser(userData: UserCreationData): Promise<AuthResponse<User>> {
    try {
      console.log('[UserService] Creating user with data:', { ...userData, password: '[REDACTED]' });
      const { email, password, role, organizationId } = userData;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            role,
            ...(organizationId ? { organization_id: organizationId } : {})
          }
        }
      });
      if (error || !data.user) {
        console.error('[UserService] User creation failed:', error);
        return {
          data: null,
          error: error || new Error('User creation failed with unknown error'),
          status: 'error',
          message: `Failed to create user: ${error?.message || 'Unknown error'}`
        };
      }
      console.log('[UserService] User created successfully:', data.user.id);
      return {
        data: data.user,
        error: null,
        status: 'success'
      };
    } catch (error) {
      console.error('[UserService] Unexpected error during user creation:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error(String(error)),
        status: 'error',
        message: `Unexpected error during user creation: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }

  /**
   * Update user profile data
   */
  static async updateUserProfile(
    userId: string, 
    profileData: UserProfileData
  ): Promise<AuthResponse<UserProfileData>> {
    try {
      console.log('[UserService] Updating user profile:', { userId, profileData });
      
      // First, update the user metadata
      const { data: authData, error: authError } = await supabase.auth.updateUser({
        data: profileData
      });
      
      if (authError) {
        console.error('[UserService] Failed to update user metadata:', authError);
        return {
          data: null,
          error: authError,
          status: 'error',
          message: `Failed to update user metadata: ${authError.message}`
        };
      }
      
      console.log('[UserService] User profile updated successfully');
      return {
        data: {
          ...profileData,
          id: userId // This is now valid since we added id to UserProfileData
        },
        error: null,
        status: 'success'
      };
    } catch (error) {
      console.error('[UserService] Unexpected error updating user profile:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error(String(error)),
        status: 'error',
        message: `Unexpected error updating user profile: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }
}

// Export the functions for easier imports in other files
export const createUser = UserService.createUser.bind(UserService);
export const updateUserProfile = UserService.updateUserProfile.bind(UserService);
